import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { PERMISSION_GROUPS } from '@/data/permissions';
import { toast } from 'sonner';

interface User {
  id: string;
  username: string;
  role: string;
  permissionGroups: string[];
  createdAt: number;
}

interface UserPermissionEditorProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
}

export function UserPermissionEditor({ user, isOpen, onClose }: UserPermissionEditorProps) {
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setSelectedGroups(user.permissionGroups || []);
    } else {
      setSelectedGroups([]);
    }
  }, [user]);

  const handleToggleGroup = (groupId: string) => {
    setSelectedGroups(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(g => g !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  const handleSubmit = async () => {
    if (!user) return;

    // 确保至少有 basic 权限
    const finalGroups = selectedGroups.length === 0 ? ['basic'] : selectedGroups;
    if (!finalGroups.includes('basic')) {
      finalGroups.push('basic');
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`/api/admin/users/${user.id}/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          permissionGroups: finalGroups,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || '更新失败');
      }

      toast.success('权限更新成功');
      onClose();
    } catch (error) {
      console.error('Update permissions error:', error);
      toast.error(error instanceof Error ? error.message : '更新权限失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>编辑用户权限</DialogTitle>
          <DialogDescription>
            为用户 <span className="font-semibold">{user.username}</span> 分配权限组
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* 用户信息 */}
          <div className="space-y-2">
            <div className="text-sm">
              <span className="text-muted-foreground">用户 ID: </span>
              <span className="font-mono">{user.id}</span>
            </div>
            <div className="text-sm">
              <span className="text-muted-foreground">角色: </span>
              <span className="font-semibold">{user.role === 'admin' ? '管理员' : '普通用户'}</span>
            </div>
          </div>

          {/* 权限组选择 */}
          <div className="space-y-4">
            <Label>权限组</Label>
            <div className="space-y-3">
              {Object.entries(PERMISSION_GROUPS).map(([key, group]) => {
                const isSelected = selectedGroups.includes(key);
                const isBasic = key === 'basic';
                const isAdmin = key === 'admin';

                return (
                  <div
                    key={key}
                    className="flex items-start space-x-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      id={`permission-${key}`}
                      checked={isSelected}
                      onChange={() => handleToggleGroup(key)}
                      disabled={isBasic || (isAdmin && user.role !== 'admin')}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
                    />
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor={`permission-${key}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {group.name}
                        <span className="ml-2 text-xs text-muted-foreground">
                          (Level {group.level})
                        </span>
                      </label>
                      <div className="text-xs text-muted-foreground">
                        {group.permissions && group.permissions.length > 0 && (
                          <div className="mt-1">
                            权限: {group.permissions.join(', ')}
                          </div>
                        )}
                        {group.includes && group.includes.length > 0 && (
                          <div className="mt-1">
                            包含: {group.includes.join(', ')}
                          </div>
                        )}
                      </div>
                      {isBasic && (
                        <div className="text-xs text-muted-foreground italic">
                          * 所有用户默认拥有此权限
                        </div>
                      )}
                      {isAdmin && user.role !== 'admin' && (
                        <div className="text-xs text-muted-foreground italic">
                          * 只有管理员角色才能拥有此权限
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 提示信息 */}
          <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
            <p className="font-semibold mb-1">权限继承说明：</p>
            <ul className="list-disc list-inside space-y-1">
              <li>basic: 基础权限，所有用户默认拥有</li>
              <li>premium: 包含 basic，可查看高级 Prompt</li>
              <li>contributor: 包含 premium，可创建和编辑自己的 Prompt</li>
              <li>admin: 包含所有权限，拥有完全控制权</li>
            </ul>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? '保存中...' : '保存'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

